import React, { useEffect, useState } from 'react';
import { invoicesAPI, transactionsAPI } from './api';
import { isIntrastate } from './gstUtils';

const REPORT_META = {
  invoices: {
    label: 'Invoice Register',
    format: 'CSV',
    dateField: 'invoice date',
    description: 'Client, tax and payment details for each invoice.',
    empty: 'No invoices match the selected period.',
  },
  gst: {
    label: 'GST Report',
    format: 'CSV',
    dateField: 'invoice date',
    description: 'Taxable invoices only, with GST-ready columns.',
    empty: 'No taxable invoices match the selected period.',
  },
  tds: {
    label: 'TDS Report',
    format: 'CSV',
    dateField: 'payment date',
    description: 'Domestic invoice receipts where the client deducted TDS.',
    empty: 'No invoice receipts with TDS match the selected period.',
  },
  ledger: {
    label: 'Bank Ledger',
    format: 'CSV',
    dateField: 'transaction date',
    description: 'Debit and credit entries with opening and running balance.',
    empty: 'No ledger entries match the selected period.',
  },
  tally: {
    label: 'Tally Export',
    format: 'XML',
    dateField: 'invoice date',
    description: 'Sales vouchers for import into Tally.',
    empty: 'No invoices are available for Tally export in this period.',
  },
};

const PERIOD_OPTIONS = [
  ['all',     'All Time'],
  ['daily',   'Daily'],
  ['monthly', 'Monthly'],
  ['custom',  'Custom Range'],
  ['fy',      'Financial Year'],
];

const FILE_BASE = {
  invoices: 'invoice_register',
  gst: 'gst_report',
  tds: 'tds_report',
  ledger: 'bank_ledger',
  tally: 'tally_sales',
};

const REPORT_ROW_LIMIT = 100000;

const TODAY = () => new Date().toISOString().split('T')[0];

const esc = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
const toCSV = (headers, rows) =>
  [headers.map(esc), ...rows.map(row => row.map(esc))].map(row => row.join(',')).join('\n');

const downloadBlob = (content, filename, mime = 'text/csv;charset=utf-8;') => {
  const bom = mime.includes('csv') ? '\uFEFF' : '';
  const blob = new Blob([bom + content], { type: mime });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
};

const amount = value => parseFloat(value || 0) || 0;
const settledAmount = row => amount(row.payment_amount) + amount(row.tds_amount);
const outstandingAmount = row => Math.max(amount(row.total) - settledAmount(row), 0);
const deriveTdsRate = row => {
  const storedRate = amount(row.tds_rate);
  if (storedRate > 0) return storedRate;
  const base = amount(row.subtotal);
  return base > 0 && amount(row.tds_amount) > 0
    ? (amount(row.tds_amount) / base) * 100
    : 0;
};
const formatRateLabel = value => {
  const rate = amount(value);
  return Number.isInteger(rate) ? String(rate) : rate.toFixed(2).replace(/\.?0+$/, '');
};
const formatPercentLabel = value => {
  const rate = amount(value);
  return rate <= 0
    ? '0'
    : (Number.isInteger(rate) ? String(rate) : rate.toFixed(2).replace(/\.?0+$/, ''));
};

const formatDate = value => (
  value
    ? new Date(`${value}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—'
);

const formatAmount = value =>
  amount(value).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const getCurrentFinancialYearStart = () => {
  const now = new Date();
  return now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
};

const formatFinancialYearLabel = startYear => `FY ${startYear}-${String(startYear + 1).slice(-2)}`;

const getFinancialYearRange = startYear => ({
  start: `${startYear}-04-01`,
  end: `${startYear + 1}-03-31`,
  label: formatFinancialYearLabel(startYear),
  filenamePart: `fy-${startYear}-${startYear + 1}`,
});

const buildFinancialYearOptions = (count = 6) => {
  const currentStart = getCurrentFinancialYearStart();
  return Array.from({ length: count }, (_, idx) => {
    const startYear = currentStart - idx;
    const range = getFinancialYearRange(startYear);
    return { value: String(startYear), label: range.label, ...range };
  });
};

const resolveRange = ({ periodMode, dailyDate, monthlyDate, startDate, endDate, fiscalYearStart }) => {
  if (periodMode === 'all') {
    return { start: null, end: null, label: 'All Time', filenamePart: 'all-time', error: '' };
  }
  if (periodMode === 'daily') {
    if (!dailyDate) return { error: 'Select a report date.' };
    return {
      start: dailyDate,
      end: dailyDate,
      label: `Daily · ${formatDate(dailyDate)}`,
      filenamePart: dailyDate,
      error: '',
    };
  }
  if (periodMode === 'monthly') {
    if (!monthlyDate) return { error: 'Select a month.' };
    const [year, month] = monthlyDate.split('-').map(Number);
    const start = `${year}-${String(month).padStart(2, '0')}-01`;
    const lastDay = new Date(year, month, 0).getDate();
    const end = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    const label = new Date(year, month - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
    return { start, end, label: `Monthly · ${label}`, filenamePart: monthlyDate, error: '' };
  }
  if (periodMode === 'custom') {
    if (!startDate || !endDate) return { error: 'Select both start and end dates.' };
    if (endDate < startDate) return { error: 'End date must be on or after start date.' };
    return {
      start: startDate,
      end: endDate,
      label: `${formatDate(startDate)} to ${formatDate(endDate)}`,
      filenamePart: `${startDate}_to_${endDate}`,
      error: '',
    };
  }
  if (!fiscalYearStart) return { error: 'Select a financial year.' };
  return { ...getFinancialYearRange(parseInt(fiscalYearStart, 10)), error: '' };
};

const inRange = (value, range) => {
  if (!range.start && !range.end) return true;
  if (!value) return false;
  if (range.start && value < range.start) return false;
  if (range.end && value > range.end) return false;
  return true;
};

const sortByDateAsc = (a, b, dateField) => {
  const leftDate = a[dateField] || '';
  const rightDate = b[dateField] || '';
  if (leftDate !== rightDate) return leftDate.localeCompare(rightDate);
  return String(a.created_at || '').localeCompare(String(b.created_at || ''));
};

const summarizeInvoiceCurrencies = rows => {
  const map = rows.reduce((acc, row) => {
    const currency = row.currency || 'INR';
    if (!acc[currency]) {
      acc[currency] = { currency, taxable: 0, gst: 0, total: 0, paid: 0, tds: 0, settled: 0 };
    }
    acc[currency].taxable += amount(row.subtotal);
    acc[currency].gst += amount(row.gst_amount);
    acc[currency].total += amount(row.total);
    acc[currency].paid += amount(row.payment_amount || 0);
    acc[currency].tds += amount(row.tds_amount || 0);
    acc[currency].settled += settledAmount(row);
    return acc;
  }, {});
  return Object.values(map).sort((a, b) => a.currency.localeCompare(b.currency));
};

const summarizeLedgerCurrencies = rows => {
  const map = rows.reduce((acc, row) => {
    const currency = row.currency || 'INR';
    if (!acc[currency]) {
      acc[currency] = { currency, credits: 0, debits: 0, net: 0 };
    }
    if (row.type === 'credit') acc[currency].credits += amount(row.amount);
    if (row.type === 'debit') acc[currency].debits += amount(row.amount);
    acc[currency].net = acc[currency].credits - acc[currency].debits;
    return acc;
  }, {});
  return Object.values(map).sort((a, b) => a.currency.localeCompare(b.currency));
};

const computeLedgerView = (allTransactions, openingBalance, range) => {
  const ordered = [...allTransactions].sort((a, b) => sortByDateAsc(a, b, 'tx_date'));
  let running = amount(openingBalance);

  if (range.start) {
    ordered.forEach(tx => {
      if (tx.tx_date && tx.tx_date < range.start) {
        running += tx.type === 'credit' ? amount(tx.amount) : -amount(tx.amount);
      }
    });
  }

  const opening = running;
  const rows = ordered
    .filter(tx => inRange(tx.tx_date, range))
    .map(tx => {
      running += tx.type === 'credit' ? amount(tx.amount) : -amount(tx.amount);
      return {
        ...tx,
        debit: tx.type === 'debit' ? amount(tx.amount) : 0,
        credit: tx.type === 'credit' ? amount(tx.amount) : 0,
        runningBalance: running,
      };
    });

  return {
    openingBalance: opening,
    closingBalance: running,
    rows,
    currencyBreakdown: summarizeLedgerCurrencies(rows),
    hasMixedCurrencies: new Set(rows.map(row => row.currency || 'INR')).size > 1,
  };
};

const buildInvoiceCsv = rows => {
  const headers = [
    'Invoice #',
    'Invoice Date',
    'Client',
    'Agreement #',
    'Place of Supply',
    'Currency',
    'Taxable Amount',
    'GST Rate %',
    'GST Type',
    'GST Amount',
    'Total',
    'Payment Status',
    'Bank Amount Received',
    'TDS Deducted',
    'TDS Rate %',
    'Settled Against Invoice',
    'Outstanding Balance',
    'Payment Date',
    'Payment Mode',
  ];
  const data = [...rows]
    .sort((a, b) => sortByDateAsc(a, b, 'invoice_date'))
    .map(row => [
      row.invoice_number,
      row.invoice_date || '',
      row.client_name || '',
      row.agreement_number || '',
      row.place_of_supply || '',
      row.currency || 'INR',
      row.subtotal,
      row.gst_rate,
      row.gst_type || 'exclusive',
      row.gst_amount,
      row.total,
      row.payment_status || 'unpaid',
      row.payment_amount || '',
      row.tds_amount || '',
      formatPercentLabel(deriveTdsRate(row)),
      settledAmount(row).toFixed(2),
      outstandingAmount(row).toFixed(2),
      row.payment_date || '',
      row.payment_mode || '',
    ]);
  return toCSV(headers, data);
};

const buildGstCsv = rows => {
  const headers = [
    'Invoice #',
    'Invoice Date',
    'Client Name',
    'Place of Supply',
    'Currency',
    'Taxable Value',
    'GST Rate %',
    'GST Type',
    'GST Amount',
    'Total Invoice Value',
    'Payment Status',
    'TDS Deducted',
    'TDS Rate %',
  ];
  const data = [...rows]
    .sort((a, b) => sortByDateAsc(a, b, 'invoice_date'))
    .map(row => [
      row.invoice_number,
      row.invoice_date || '',
      row.client_name || '',
      row.place_of_supply || '',
      row.currency || 'INR',
      row.subtotal,
      row.gst_rate,
      row.gst_type || 'exclusive',
      row.gst_amount,
      row.total,
      row.payment_status || 'unpaid',
      row.tds_amount || '',
      formatPercentLabel(deriveTdsRate(row)),
    ]);
  return toCSV(headers, data);
};

const buildTdsCsv = rows => {
  const headers = [
    'Invoice #',
    'Payment Date',
    'Invoice Date',
    'Client Name',
    'Agreement #',
    'Place of Supply',
    'Currency',
    'Taxable Value',
    'GST Amount',
    'Invoice Total',
    'Bank Amount Received',
    'TDS Deducted',
    'TDS Rate %',
    'Settled Against Invoice',
    'Outstanding Balance',
    'Payment Status',
    'Payment Mode',
  ];
  const data = [...rows]
    .sort((a, b) => sortByDateAsc(a, b, 'payment_date'))
    .map(row => [
      row.invoice_number,
      row.payment_date || '',
      row.invoice_date || '',
      row.client_name || '',
      row.agreement_number || '',
      row.place_of_supply || '',
      row.currency || 'INR',
      row.subtotal,
      row.gst_amount,
      row.total,
      row.payment_amount || '',
      row.tds_amount || '',
      formatPercentLabel(deriveTdsRate(row)),
      settledAmount(row).toFixed(2),
      outstandingAmount(row).toFixed(2),
      row.payment_status || 'unpaid',
      row.payment_mode || '',
    ]);
  return toCSV(headers, data);
};

const buildLedgerCsv = ({ openingBalance, closingBalance, rows }) => {
  const headers = [
    'Date',
    'Entry Type',
    'Category',
    'Description',
    'Reference/Invoice #',
    'Currency',
    'Debit',
    'Credit',
    'Running Balance',
  ];
  const data = [
    ['', 'opening', '', 'Opening Balance', '', '', '', '', openingBalance.toFixed(2)],
    ...rows.map(row => [
      row.tx_date || '',
      row.type,
      row.category || '',
      row.description || '',
      row.invoice_number || row.reference || '',
      row.currency || 'INR',
      row.debit ? row.debit.toFixed(2) : '',
      row.credit ? row.credit.toFixed(2) : '',
      row.runningBalance.toFixed(2),
    ]),
    ['', 'closing', '', 'Closing Balance', '', '', '', '', closingBalance.toFixed(2)],
  ];
  return toCSV(headers, data);
};

const xmlEscape = value => String(value ?? '').replace(/[<>&"]/g, char => ({
  '<': '&lt;',
  '>': '&gt;',
  '&': '&amp;',
  '"': '&quot;',
}[char]));

const buildTallyXml = (rows, firmName, firmGstin = '') => {
  const lines = [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<ENVELOPE>',
    '  <HEADER>',
    '    <VERSION>1</VERSION>',
    '    <TALLYREQUEST>Import</TALLYREQUEST>',
    '    <TYPE>Data</TYPE>',
    '    <ID>Vouchers</ID>',
    '  </HEADER>',
    '  <BODY>',
    '    <DESC>',
    '      <STATICVARIABLES>',
    `        <SVCURRENTCOMPANY>${xmlEscape(firmName)}</SVCURRENTCOMPANY>`,
    '      </STATICVARIABLES>',
    '    </DESC>',
    '    <DATA>',
  ];

  [...rows]
    .sort((a, b) => sortByDateAsc(a, b, 'invoice_date'))
    .forEach(inv => {
      const date = (inv.invoice_date || TODAY()).replace(/-/g, '');
      const total = amount(inv.total).toFixed(2);
      const subtotal = amount(inv.subtotal).toFixed(2);
      const gst = amount(inv.gst_amount).toFixed(2);
      const client = xmlEscape(inv.client_name || 'Sundry Debtors');
      const invoiceNumber = xmlEscape(inv.invoice_number || '');
      const rateLabel = formatRateLabel(inv.gst_rate);
      const invIntrastate = isIntrastate(firmGstin, inv.place_of_supply);
      const salesLedger = xmlEscape(amount(inv.gst_rate) > 0 ? `Sales @${rateLabel}%` : 'Sales');
      const gstLedger = xmlEscape(`GST @${rateLabel}%`);
      lines.push(
        '      <TALLYMESSAGE>',
        '        <VOUCHER VCHTYPE="Sales" ACTION="Create" OBJVIEW="Invoice Voucher View">',
        `          <DATE>${date}</DATE>`,
        '          <VOUCHERTYPENAME>Sales</VOUCHERTYPENAME>',
        `          <VOUCHERNUMBER>${invoiceNumber}</VOUCHERNUMBER>`,
        '          <PERSISTEDVIEW>Invoice Voucher View</PERSISTEDVIEW>',
        '          <ISINVOICE>Yes</ISINVOICE>',
        '          <OBJVIEW>Invoice Voucher View</OBJVIEW>',
        `          <NARRATION>Invoice ${invoiceNumber} - ${client}</NARRATION>`,
        '          <LEDGERENTRIES.LIST>',
        `            <LEDGERNAME>${client}</LEDGERNAME>`,
        '            <ISDEEMEDPOSITIVE>Yes</ISDEEMEDPOSITIVE>',
        '            <ISPARTYLEDGER>Yes</ISPARTYLEDGER>',
        '            <ISLASTDEEMEDPOSITIVE>Yes</ISLASTDEEMEDPOSITIVE>',
        `            <AMOUNT>-${total}</AMOUNT>`,
        '            <BILLALLOCATIONS.LIST>',
        `              <NAME>${invoiceNumber}</NAME>`,
        '              <BILLTYPE>New Ref</BILLTYPE>',
        `              <AMOUNT>-${total}</AMOUNT>`,
        '            </BILLALLOCATIONS.LIST>',
        '          </LEDGERENTRIES.LIST>',
        '          <LEDGERENTRIES.LIST>',
        `            <LEDGERNAME>${salesLedger}</LEDGERNAME>`,
        '            <ISDEEMEDPOSITIVE>No</ISDEEMEDPOSITIVE>',
        `            <AMOUNT>${subtotal}</AMOUNT>`,
        '          </LEDGERENTRIES.LIST>',
      );
      if (amount(gst) > 0) {
        if (invIntrastate) {
          const halfGst = (amount(gst) / 2).toFixed(2);
          const halfRate = parseFloat(rateLabel) / 2;
          lines.push(
            '          <LEDGERENTRIES.LIST>',
            `            <LEDGERNAME>${xmlEscape(`CGST @${halfRate}%`)}</LEDGERNAME>`,
            '            <ISDEEMEDPOSITIVE>No</ISDEEMEDPOSITIVE>',
            `            <AMOUNT>${halfGst}</AMOUNT>`,
            '          </LEDGERENTRIES.LIST>',
            '          <LEDGERENTRIES.LIST>',
            `            <LEDGERNAME>${xmlEscape(`SGST @${halfRate}%`)}</LEDGERNAME>`,
            '            <ISDEEMEDPOSITIVE>No</ISDEEMEDPOSITIVE>',
            `            <AMOUNT>${halfGst}</AMOUNT>`,
            '          </LEDGERENTRIES.LIST>',
          );
        } else {
          lines.push(
            '          <LEDGERENTRIES.LIST>',
            `            <LEDGERNAME>${gstLedger}</LEDGERNAME>`,
            '            <ISDEEMEDPOSITIVE>No</ISDEEMEDPOSITIVE>',
            `            <AMOUNT>${gst}</AMOUNT>`,
            '          </LEDGERENTRIES.LIST>',
          );
        }
      }
      lines.push(
        '        </VOUCHER>',
        '      </TALLYMESSAGE>',
      );
    });

  lines.push('    </DATA>', '  </BODY>', '</ENVELOPE>');
  return lines.join('\n');
};

const buildFilename = (reportKey, range, extension) =>
  `${FILE_BASE[reportKey]}_${range.filenamePart}_${TODAY()}.${extension}`;

export default function ReportsModal({ firm, initialReport = 'invoices', onClose }) {
  const fyOptions = buildFinancialYearOptions();
  const [reportKey, setReportKey] = useState(initialReport);
  const [periodMode, setPeriodMode] = useState('all');
  const [dailyDate, setDailyDate] = useState(TODAY());
  const [monthlyDate, setMonthlyDate] = useState(TODAY().slice(0, 7)); // YYYY-MM
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [fiscalYearStart, setFiscalYearStart] = useState(fyOptions[0]?.value || '');
  const [invoices, setInvoices] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [invoiceLoading, setInvoiceLoading] = useState(true);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [invoiceError, setInvoiceError] = useState('');
  const [transactionsError, setTransactionsError] = useState('');
  const [transactionsLoaded, setTransactionsLoaded] = useState(false);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setInvoiceLoading(true);
      setInvoiceError('');
      try {
        const invoiceRows = await invoicesAPI.list();
        if (!active) return;
        setInvoices(invoiceRows);
      } catch (err) {
        if (!active) return;
        setInvoiceError(err.message || 'Unable to load invoice report data.');
      } finally {
        if (active) setInvoiceLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (reportKey !== 'ledger' || transactionsLoaded || transactionsLoading) return undefined;

    let active = true;
    const loadTransactions = async () => {
      setTransactionsLoading(true);
      setTransactionsError('');
      try {
        const rows = await transactionsAPI.list({ limit: REPORT_ROW_LIMIT, offset: 0 });
        if (!active) return;
        setTransactions(rows);
        setTransactionsLoaded(true);
      } catch (err) {
        if (!active) return;
        setTransactionsError(err.message || 'Unable to load ledger report data.');
      } finally {
        if (active) setTransactionsLoading(false);
      }
    };

    loadTransactions();
    return () => { active = false; };
  }, [reportKey, transactionsLoaded, transactionsLoading]);

  const range = resolveRange({ periodMode, dailyDate, monthlyDate, startDate, endDate, fiscalYearStart });
  const filteredInvoices = invoices.filter(row => inRange(row.invoice_date, range));
  const filteredTaxInvoices = filteredInvoices.filter(row => amount(row.gst_amount) > 0 || amount(row.gst_rate) > 0);
  const filteredTdsInvoices = invoices
    .filter(row => (row.currency || 'INR') === 'INR' && amount(row.tds_amount) > 0)
    .filter(row => inRange(row.payment_date, range));
  const ledgerView = computeLedgerView(transactions, firm?.bankBalanceOpening || 0, range);
  const loading = invoiceLoading || (reportKey === 'ledger' && transactionsLoading);
  const error = reportKey === 'ledger'
    ? (invoiceError || transactionsError)
    : invoiceError;

  let preview = {
    cards: [],
    breakdownHeaders: [],
    breakdownRows: [],
    note: '',
    recordCount: 0,
    empty: REPORT_META[reportKey].empty,
  };

  if (reportKey === 'invoices') {
    preview = {
      cards: [
        { label: 'Invoices', value: filteredInvoices.length },
        { label: 'Taxable', value: filteredInvoices.filter(row => amount(row.gst_amount) > 0).length },
        { label: 'Paid / Partial', value: filteredInvoices.filter(row => ['paid', 'partial'].includes(row.payment_status)).length },
        { label: 'Unpaid', value: filteredInvoices.filter(row => (row.payment_status || 'unpaid') === 'unpaid').length },
      ],
      breakdownHeaders: ['Currency', 'Taxable', 'GST', 'Gross', 'Bank Received', 'TDS', 'Settled'],
      breakdownRows: summarizeInvoiceCurrencies(filteredInvoices).map(row => [
        row.currency,
        formatAmount(row.taxable),
        formatAmount(row.gst),
        formatAmount(row.total),
        formatAmount(row.paid),
        formatAmount(row.tds),
        formatAmount(row.settled),
      ]),
      note: '',
      recordCount: filteredInvoices.length,
      empty: REPORT_META.invoices.empty,
    };
  }

  if (reportKey === 'gst') {
    preview = {
      cards: [
        { label: 'Taxable Invoices', value: filteredTaxInvoices.length },
        { label: 'Distinct Clients', value: new Set(filteredTaxInvoices.map(row => row.client_name).filter(Boolean)).size },
        { label: 'Domestic / Export Mix', value: `${filteredTaxInvoices.filter(row => (row.currency || 'INR') === 'INR').length}/${filteredTaxInvoices.filter(row => (row.currency || 'INR') !== 'INR').length}` },
        { label: 'Unpaid', value: filteredTaxInvoices.filter(row => (row.payment_status || 'unpaid') === 'unpaid').length },
      ],
      breakdownHeaders: ['Currency', 'Taxable', 'GST', 'Gross', 'TDS Deducted'],
      breakdownRows: summarizeInvoiceCurrencies(filteredTaxInvoices).map(row => [
        row.currency,
        formatAmount(row.taxable),
        formatAmount(row.gst),
        formatAmount(row.total),
        formatAmount(row.tds),
      ]),
      note: 'GST export uses the stored GST rate, GST type, and GST amount from each invoice.',
      recordCount: filteredTaxInvoices.length,
      empty: REPORT_META.gst.empty,
    };
  }

  if (reportKey === 'tds') {
    preview = {
      cards: [
        { label: 'Invoices with TDS', value: filteredTdsInvoices.length },
        { label: 'Distinct Clients', value: new Set(filteredTdsInvoices.map(row => row.client_name).filter(Boolean)).size },
        { label: 'Total TDS Deducted', value: formatAmount(filteredTdsInvoices.reduce((sum, row) => sum + amount(row.tds_amount), 0)) },
        { label: 'Outstanding', value: formatAmount(filteredTdsInvoices.reduce((sum, row) => sum + outstandingAmount(row), 0)) },
      ],
      breakdownHeaders: ['Currency', 'Taxable', 'TDS', 'Bank Received', 'Settled', 'Outstanding'],
      breakdownRows: summarizeInvoiceCurrencies(filteredTdsInvoices).map(row => [
        row.currency,
        formatAmount(row.taxable),
        formatAmount(row.tds),
        formatAmount(row.paid),
        formatAmount(row.settled),
        formatAmount(filteredTdsInvoices
          .filter(invoice => (invoice.currency || 'INR') === row.currency)
          .reduce((sum, invoice) => sum + outstandingAmount(invoice), 0)),
      ]),
      note: 'TDS is reported on payment date, because deduction happens when the client settles the invoice.',
      recordCount: filteredTdsInvoices.length,
      empty: REPORT_META.tds.empty,
    };
  }

  if (reportKey === 'ledger') {
    preview = {
      cards: [
        { label: 'Entries', value: ledgerView.rows.length },
        { label: 'Credits', value: ledgerView.rows.filter(row => row.type === 'credit').length },
        { label: 'Debits', value: ledgerView.rows.filter(row => row.type === 'debit').length },
        { label: 'Opening Balance', value: formatAmount(ledgerView.openingBalance) },
      ],
      breakdownHeaders: ['Currency', 'Credits', 'Debits', 'Net'],
      breakdownRows: ledgerView.currencyBreakdown.map(row => [
        row.currency,
        formatAmount(row.credits),
        formatAmount(row.debits),
        formatAmount(row.net),
      ]),
      note: ledgerView.hasMixedCurrencies
        ? 'This ledger contains multiple currencies. Running balance is exported as stored, so use the currency column row by row.'
        : `Closing balance for this period: ${formatAmount(ledgerView.closingBalance)}`,
      recordCount: ledgerView.rows.length,
      empty: REPORT_META.ledger.empty,
    };
  }

  if (reportKey === 'tally') {
    preview = {
      cards: [
        { label: 'Invoices', value: filteredInvoices.length },
        { label: 'Taxable', value: filteredInvoices.filter(row => amount(row.gst_amount) > 0).length },
        { label: 'Zero-GST', value: filteredInvoices.filter(row => amount(row.gst_amount) === 0).length },
        { label: 'Distinct Clients', value: new Set(filteredInvoices.map(row => row.client_name).filter(Boolean)).size },
      ],
      breakdownHeaders: ['Currency', 'Sales', 'GST', 'Gross'],
      breakdownRows: summarizeInvoiceCurrencies(filteredInvoices).map(row => [
        row.currency,
        formatAmount(row.taxable),
        formatAmount(row.gst),
        formatAmount(row.total),
      ]),
      note: 'This XML is generated as a Sales invoice voucher with bill allocation. Matching party, sales, and GST ledgers should exist in Tally before import.',
      recordCount: filteredInvoices.length,
      empty: REPORT_META.tally.empty,
    };
  }

  const canDownload = !loading && !error && !range.error && preview.recordCount > 0;

  const downloadReport = () => {
    if (!canDownload) return;
    if (reportKey === 'invoices') {
      downloadBlob(
        buildInvoiceCsv(filteredInvoices),
        buildFilename('invoices', range, 'csv'),
      );
      return;
    }
    if (reportKey === 'gst') {
      downloadBlob(
        buildGstCsv(filteredTaxInvoices),
        buildFilename('gst', range, 'csv'),
      );
      return;
    }
    if (reportKey === 'tds') {
      downloadBlob(
        buildTdsCsv(filteredTdsInvoices),
        buildFilename('tds', range, 'csv'),
      );
      return;
    }
    if (reportKey === 'ledger') {
      downloadBlob(
        buildLedgerCsv(ledgerView),
        buildFilename('ledger', range, 'csv'),
      );
      return;
    }
    downloadBlob(
      buildTallyXml(filteredInvoices, firm?.name || '', firm?.gstNumber || ''),
      buildFilename('tally', range, 'xml'),
      'application/xml;charset=utf-8;',
    );
  };

  return (
    <div className="modal-overlay reports-overlay" onClick={event => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="reports-modal-box">
        <div className="modal-header">
          <div>
            <h3 className="modal-title">Report Center</h3>
            <div className="modal-inv-ref">Download daily, monthly, custom-range, and financial-year reports.</div>
          </div>
          <button className="cp-close" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="reports-modal-body">
          <div className="report-type-grid">
            {Object.entries(REPORT_META).map(([key, meta]) => (
              <button
                key={key}
                className={`report-type-card${reportKey === key ? ' active' : ''}`}
                onClick={() => setReportKey(key)}
              >
                <div className="report-type-format">{meta.format}</div>
                <div className="report-type-label">{meta.label}</div>
                <div className="report-type-desc">{meta.description}</div>
              </button>
            ))}
          </div>

          <div className="report-filter-card">
            <div className="report-filter-head">
              <div>
                <div className="report-filter-title">Period Filter</div>
                <div className="report-filter-sub">
                  This report uses {REPORT_META[reportKey].dateField} for filtering.
                </div>
              </div>
              <div className="report-range-chip">{range.label || 'Select period'}</div>
            </div>

            <div className="report-period-toggle">
              {PERIOD_OPTIONS.map(([key, label]) => (
                <button
                  key={key}
                  className={`report-period-btn${periodMode === key ? ' active' : ''}`}
                  onClick={() => setPeriodMode(key)}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="report-filter-grid">
              {periodMode === 'daily' && (
                <div className="modal-fg">
                  <label className="modal-lbl">Report Date</label>
                  <input className="modal-inp" type="date" value={dailyDate} onChange={event => setDailyDate(event.target.value)} />
                </div>
              )}

              {periodMode === 'monthly' && (
                <div className="modal-fg">
                  <label className="modal-lbl">Month</label>
                  <input className="modal-inp" type="month" value={monthlyDate} onChange={event => setMonthlyDate(event.target.value)} />
                </div>
              )}

              {periodMode === 'custom' && (
                <>
                  <div className="modal-fg">
                    <label className="modal-lbl">Start Date</label>
                    <input className="modal-inp" type="date" value={startDate} onChange={event => setStartDate(event.target.value)} />
                  </div>
                  <div className="modal-fg">
                    <label className="modal-lbl">End Date</label>
                    <input className="modal-inp" type="date" value={endDate} onChange={event => setEndDate(event.target.value)} />
                  </div>
                </>
              )}

              {periodMode === 'fy' && (
                <div className="modal-fg">
                  <label className="modal-lbl">Financial Year</label>
                  <select className="modal-inp" value={fiscalYearStart} onChange={event => setFiscalYearStart(event.target.value)}>
                    {fyOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {range.error && <div className="modal-error">{range.error}</div>}
          </div>

          <div className="report-preview-card">
            <div className="report-preview-head">
              <div>
                <div className="report-preview-title">{REPORT_META[reportKey].label}</div>
                <div className="report-preview-sub">{REPORT_META[reportKey].description}</div>
              </div>
              <div className="report-preview-meta">{preview.recordCount} record{preview.recordCount === 1 ? '' : 's'}</div>
            </div>

            {loading && <div className="report-state">Loading report data…</div>}
            {!loading && error && <div className="report-state report-state-error">{error}</div>}
            {!loading && !error && !range.error && preview.recordCount === 0 && (
              <div className="report-empty">{preview.empty}</div>
            )}

            {!loading && !error && !range.error && preview.recordCount > 0 && (
              <>
                <div className="report-summary-grid">
                  {preview.cards.map(card => (
                    <div key={card.label} className="report-summary-item">
                      <span>{card.label}</span>
                      <strong>{card.value}</strong>
                    </div>
                  ))}
                </div>

                {preview.breakdownRows.length > 0 && (
                  <div className="report-breakdown-wrap">
                    <div className="report-breakdown-title">Totals by Currency</div>
                    <div className="report-breakdown-table-wrap">
                      <table className="report-breakdown-table">
                        <thead>
                          <tr>
                            {preview.breakdownHeaders.map(header => <th key={header}>{header}</th>)}
                          </tr>
                        </thead>
                        <tbody>
                          {preview.breakdownRows.map((row, index) => (
                            <tr key={`${row[0]}-${index}`}>
                              {row.map((cell, cellIndex) => <td key={`${row[0]}-${cellIndex}`}>{cell}</td>)}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {preview.note && <div className="report-note">{preview.note}</div>}
              </>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button type="button" className="modal-cancel" onClick={onClose}>Close</button>
          <button type="button" className="modal-save" onClick={downloadReport} disabled={!canDownload}>
            Download {REPORT_META[reportKey].format}
          </button>
        </div>
      </div>
    </div>
  );
}
